import { useState, useEffect, useCallback } from "react";
import Icon from "../../components/ui/Icon";
import SlideOverlay from "../../components/overlays/SlideOverlay";
import PageHeader from "../../components/shared/PageHeader";
import { useAppState } from "../../state/appState";
import { FIRMWARE_CATALOG } from "../../constants/firmware";
import { DEVICE_TYPES } from "../../domain/models";
import { useFirmwareProgress } from "../../hooks/useFirmwareProgress";
import FirmwarePreCheck from "../firmware/FirmwarePreCheck";
import FirmwareProgress from "../firmware/FirmwareProgress";
import FirmwareDone from "../firmware/FirmwareDone";
import FirmwareChangelog from "../firmware/FirmwareChangelog";

export default function NodeDetailsOverlay({ isOpen, node, onClose }) {
  const { updatedFirmware, actions, devices } = useAppState();
  const [fwPage, setFwPage] = useState(null); // null | "pre-check" | "progress" | "done" | "changelog"
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editAddress, setEditAddress] = useState("");

  const n = node ?? {};
  const batteryDevice = n.type === "BMS"
    ? (devices || []).find((d) => d.nodeId === n.id && d.type === DEVICE_TYPES.BMS) ?? null
    : null;
  const updateEntry = FIRMWARE_CATALOG[n.id];
  const isUpdated = !!updatedFirmware?.[n.id];
  const hasUpdate = !!updateEntry && !isUpdated;
  const currentFwDisplay = isUpdated
    ? (updatedFirmware[n.id]?.version ?? n.fw ?? "—")
    : (n.fw ?? "—");
  const isOnline = (n.status ?? "OK") !== "Error";

  const handleFwComplete = useCallback(() => {
    if (updateEntry) {
      actions.markFirmwareUpdated(updateEntry.nodeId, updateEntry.availableVersion);
    }
    setFwPage("done");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateEntry?.nodeId, updateEntry?.availableVersion]);

  const { percent: fwProgress, step: fwStep } = useFirmwareProgress(
    fwPage === "progress",
    handleFwComplete
  );

  useEffect(() => {
    if (!isOpen) {
      setFwPage(null);
      setIsEditing(false);
    }
  }, [isOpen]);

  const handleBack = () => {
    if (fwPage === "progress") return;
    if (fwPage !== null) setFwPage(null);
    else onClose();
  };

  const headerTitle =
    fwPage === "pre-check" || fwPage === "progress" || fwPage === "done"
      ? "Firmware Update"
      : fwPage === "changelog"
      ? "Changelog"
      : `Node Details: ${n.name ?? "—"}`;

  return (
    <SlideOverlay isOpen={isOpen}>
      <div className="h-full overflow-y-auto bg-brand-navy px-4 pb-6">
        <div className="mx-auto w-full max-w-xl space-y-4 pt-4">

          <PageHeader
            title={headerTitle}
            onBack={handleBack}
            backDisabled={fwPage === "progress"}
          />

          {/* ── Main: Node info + Firmware + Diagnostics + Backup ──────── */}
          {!fwPage && (
            <>
              {/* Node Details Card */}
              <div className="rounded-3xl border border-brand-navy bg-brand-purple px-4 py-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-xs font-black italic tracking-widest text-brand-light/50 uppercase">
                    Node Details
                  </h2>
                  {isEditing ? (
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => {
                          actions.updateNodeMeta(n.id, {
                            name: editName.trim() || n.name,
                            address: editAddress.trim() || n.address,
                          });
                          setIsEditing(false);
                        }}
                        className="rounded-xl bg-brand-magenta px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-brand-pink transition"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsEditing(false)}
                        className="rounded-xl border border-brand-navy bg-brand-navy px-2.5 py-1 text-[11px] font-semibold text-brand-light/70 hover:border-brand-magenta/60 transition"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setEditName(n.name ?? "");
                        setEditAddress(n.address ?? "");
                        setIsEditing(true);
                      }}
                      className="flex items-center gap-1 rounded-xl border border-brand-navy bg-brand-navy px-2.5 py-1 text-[11px] font-semibold text-brand-light/70 hover:border-brand-magenta/60 hover:text-brand-magenta transition"
                    >
                      <Icon name="edit" className="text-sm" />
                      Edit
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] font-semibold tracking-widest text-brand-light/40 uppercase">Name</span>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="rounded-lg border border-brand-magenta/60 bg-brand-navy px-2 py-1 text-sm font-semibold text-brand-light outline-none focus:border-brand-magenta"
                      />
                    ) : (
                      <span className="text-sm font-semibold text-brand-light">{n.name ?? "—"}</span>
                    )}
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] font-semibold tracking-widest text-brand-light/40 uppercase">Type</span>
                    <span className="text-sm font-semibold text-brand-light">{n.type ?? "—"}</span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] font-semibold tracking-widest text-brand-light/40 uppercase">Address</span>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editAddress}
                        onChange={(e) => setEditAddress(e.target.value)}
                        className="rounded-lg border border-brand-magenta/60 bg-brand-navy px-2 py-1 text-sm font-semibold text-brand-light outline-none focus:border-brand-magenta"
                      />
                    ) : (
                      <span className="text-sm font-semibold text-brand-light">{n.address ?? "—"}</span>
                    )}
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] font-semibold tracking-widest text-brand-light/40 uppercase">Status</span>
                    <div className="flex items-center gap-1.5">
                      <Icon
                        name={isOnline ? "check_circle" : "cancel"}
                        className={`text-base ${isOnline ? "text-emerald-400" : "text-slate-500"}`}
                      />
                      <span className={`text-sm font-semibold ${isOnline ? "text-emerald-400" : "text-slate-400"}`}>
                        {isOnline ? "Online" : "Offline"}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] font-semibold tracking-widest text-brand-light/40 uppercase">HW Version</span>
                    <span className="text-sm font-semibold text-brand-light">{n.hw ?? "—"}</span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] font-semibold tracking-widest text-brand-light/40 uppercase">FW Version</span>
                    <span className="text-sm font-semibold text-brand-light">{currentFwDisplay}</span>
                  </div>
                </div>
              </div>

              {/* Connected Battery Card — only for BMS bridge nodes */}
              {batteryDevice && (
                <div className="rounded-3xl border border-brand-navy bg-brand-purple px-4 py-4 space-y-3">
                  <h2 className="text-xs font-black italic tracking-widest text-brand-light/50 uppercase">
                    Connected Battery
                  </h2>
                  <div className="rounded-2xl border border-brand-navy bg-brand-navy px-3 py-3 flex items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-purple border border-brand-navy">
                      <Icon name="battery_charging_full" className="text-xl text-brand-magenta" />
                    </div>
                    <div className="flex-1 flex flex-col gap-0.5 min-w-0">
                      <span className="text-sm font-semibold text-brand-light truncate">{batteryDevice.name}</span>
                      <span className="text-[11px] text-brand-light/50">via {n.name ?? "BMS Bridge"}</span>
                    </div>
                    <div className="flex flex-col items-end shrink-0">
                      <span className="text-xl font-black italic text-brand-magenta">
                        {batteryDevice.metrics?.soc ?? "--"}%
                      </span>
                      <span className="text-[10px] text-brand-light/50">SoC</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { label: "Voltage", value: batteryDevice.metrics?.voltage != null ? `${batteryDevice.metrics.voltage} V` : "--" },
                      { label: "Current", value: batteryDevice.metrics?.current != null ? `${batteryDevice.metrics.current > 0 ? "+" : ""}${batteryDevice.metrics.current} A` : "--" },
                      { label: "Status", value: batteryDevice.metrics?.statusLine ?? "--" },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex flex-col gap-0.5 rounded-xl border border-brand-navy bg-brand-navy px-2 py-2">
                        <span className="text-[10px] font-semibold tracking-widest text-brand-light/40 uppercase">{label}</span>
                        <span className="text-xs font-semibold text-brand-light">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Firmware Update Card */}
              <div className="rounded-3xl border border-brand-navy bg-brand-purple px-4 py-4 space-y-3">
                <h2 className="text-xs font-black italic tracking-widest text-brand-light/50 uppercase">
                  Firmware Update
                </h2>
                {hasUpdate ? (
                  <>
                    <div className="rounded-2xl border border-brand-magenta/40 bg-[#3a1558] px-3 py-2.5 flex items-center gap-2">
                      <Icon name="system_update" className="text-base text-brand-magenta" />
                      <div className="flex flex-col">
                        <span className="text-sm font-black italic text-brand-light">
                          Version {updateEntry.availableVersion} available
                        </span>
                        <span className="text-[10px] text-brand-light/40">Released {updateEntry.releaseDate}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFwPage("pre-check")}
                      className="w-full rounded-2xl bg-brand-magenta px-4 py-3 text-sm font-black italic text-white shadow-lg shadow-brand-magenta/30 hover:bg-brand-pink transition active:scale-[0.99]"
                    >
                      Update
                    </button>
                    <div className="flex items-center justify-center gap-1">
                      <button
                        type="button"
                        onClick={() => setFwPage("changelog")}
                        className="text-xs font-semibold text-brand-magenta hover:text-brand-pink transition"
                      >
                        View changelog
                      </button>
                      <Icon name="arrow_forward" className="text-sm text-brand-magenta" />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2.5 flex items-center gap-2">
                      <Icon name="check_circle" className="text-base text-emerald-400" />
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-emerald-400">Up to date</span>
                        <span className="text-[10px] text-brand-light/40">
                          {currentFwDisplay} is the latest version
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled
                      className="w-full rounded-2xl bg-brand-purple/50 border border-brand-navy px-4 py-3 text-sm font-black italic text-brand-light/30 cursor-not-allowed"
                    >
                      Update
                    </button>
                  </>
                )}
              </div>

              {/* Diagnostics Card */}
              <div className="rounded-3xl border border-brand-navy bg-brand-purple px-4 py-4 space-y-3">
                <h2 className="text-xs font-black italic tracking-widest text-brand-light/50 uppercase">
                  Diagnostics
                </h2>
                <div className="flex items-start gap-3">
                  <Icon name="warning" className="text-xl shrink-0" style={{ color: "#FF4D4F" }} />
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm font-black italic text-brand-light">1 minor issue detected</span>
                    <span className="text-[11px] text-brand-light/50">Cell voltage imbalance above threshold</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button type="button" className="text-xs font-semibold text-brand-magenta hover:text-brand-pink transition">
                    View all diagnostics
                  </button>
                  <Icon name="arrow_forward" className="text-sm text-brand-magenta" />
                </div>
              </div>

              {/* Backup & Restore */}
              <div className="rounded-3xl border border-brand-navy bg-brand-navy overflow-hidden">
                {["Create Backup", "Restore Backup"].map((label) => (
                  <button
                    key={label}
                    type="button"
                    className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-brand-purple/30 transition border-b border-brand-purple/30"
                  >
                    <span className="text-sm font-semibold text-brand-light">{label}</span>
                    <Icon name="arrow_forward" className="text-base text-brand-magenta" />
                  </button>
                ))}
                <button
                  type="button"
                  className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-brand-purple/30 transition"
                >
                  <div className="flex items-center gap-2">
                    <Icon name="restart_alt" className="text-base" style={{ color: "#FF4D4F" }} />
                    <span className="text-sm font-semibold text-brand-light">Restore Factory Defaults</span>
                  </div>
                  <Icon name="arrow_forward" className="text-base" style={{ color: "#FF4D4F" }} />
                </button>
              </div>
            </>
          )}

          {/* ── Firmware sub-pages ───────────────────────────────────────── */}
          {fwPage === "pre-check" && (
            <FirmwarePreCheck
              updateEntry={updateEntry}
              onStartUpdate={() => setFwPage("progress")}
            />
          )}
          {fwPage === "progress" && (
            <FirmwareProgress updateEntry={updateEntry} percent={fwProgress} step={fwStep} />
          )}
          {fwPage === "done" && (
            <FirmwareDone updateEntry={updateEntry} onDone={() => { setFwPage(null); onClose(); }} />
          )}
          {fwPage === "changelog" && (
            <FirmwareChangelog updateEntry={updateEntry} />
          )}

        </div>
      </div>
    </SlideOverlay>
  );
}
